import { loading as loadingSymbol } from './symbols';

import type { DivisiblePart } from './types';
import {
    Observable,
    type Observer,
    Subject,
    Subscription,
    type Unsubscribable,
} from 'rxjs';
import { useSubscription } from '@vueuse/rxjs';
import {
    computed,
    type FunctionalComponent,
    h,
    mergeProps,
    shallowRef,
} from 'vue';
import Skeleton from 'primevue/skeleton';

export default class DivisibleTarget
    extends Observable<DivisiblePart>
    implements Observer<DivisiblePart>, Unsubscribable
{
    protected subject: Subject<DivisiblePart>;
    protected subscriptions: Set<Subscription>;
    protected subscribers: number;

    constructor() {
        super((subscriber) => {
            this.subscribers++; // Increase subscriber count.
            const s = this.subject.subscribe(subscriber); // Subscribe to underlying Subject.
            subscriber.add(s); // Add the Subject-subscription to the subscriber-teardown.
            return () => {
                // Teardown logic.
                this.subscribers--; // Decrease subscriber count.
                if (!this.has())
                    // When there are no subscribers left:
                    this.unsubscribe(); // Unsubscribe this target from all sources.
            };
        });

        // Initialise state.
        this.subject = new Subject<DivisiblePart>();
        this.subscriptions = new Set<Subscription>();
        this.subscribers = 0;
    }

    // ----------------------------------------------------------------------------------------------------------- //
    //   RXJS Behaviour                                                                                            //
    // ----------------------------------------------------------------------------------------------------------- //

    protected subs() {
        return this.subscriptions;
    }

    /**
     * Removes all closed subscriptions.
     */
    public purgeSubscriptions() {
        for (const sub of this.subscriptions.values()) {
            if (sub.closed) this.subscriptions.delete(sub);
        }
    }

    /**
     * Unsubscribe from all observables.
     */
    public unsubscribe() {
        for (const sub of this.subscriptions.values()) {
            if (!sub.closed) sub.unsubscribe();
        }
        this.purgeSubscriptions();
    }

    public next(value: DivisiblePart) {
        this.subject.next(value);
    }
    public error(err: any) {
        this.subject.error(err);
    }
    public complete() {
        this.subject.complete();
    }

    public has() {
        return this.subscribers > 0;
    }

    /**
     * Remove the subject subscriptions and sends a `complete`.
     */
    public close() {
        this.unsubscribe();
        this.complete();
    }

    public give(observable: Observable<DivisiblePart>) {
        if (!this.has()) return Subscription.EMPTY; // Do nothing when there is no subscription to listen to.
        const sub = observable.subscribe(this); // Subscribe to the observable.
        this.subscriptions.add(sub); // Add sub to the subscriptions.
        sub.add(() => this.subscriptions.delete(sub)); // Remove sub from subscriptions when unsubscribed.
        useSubscription(sub); // Ensure sub is unsubscribed when the defining component is removed.
        return sub; // Return the subscription.
    }

    // ----------------------------------------------------------------------------------------------------------- //
    //   OBSERVER HELPERS                                                                                          //
    // ----------------------------------------------------------------------------------------------------------- //

    use() {
        const part = shallowRef<DivisiblePart>(null);
        const connected = shallowRef<boolean>(false);

        const subscription = this.subscribe({
            next: (p) => {
                part.value = p;
                connected.value = true;
            },
            complete: () => {
                connected.value = false;
            },
        });
        useSubscription(subscription);

        const component: FunctionalComponent<Record<string, any>> = (
            props,
            { slots }
        ) => {
            if (part.value === null) return null;
            if (part.value === loadingSymbol) {
                const skeletonProps = mergeProps(
                    {
                        class: props.skeletonClass,
                        style: props.skeletonStyle,
                    },
                    props.skeletonProps ?? {}
                );
                if (slots.skeleton) return slots.skeleton(skeletonProps);
                return h(Skeleton, skeletonProps);
            }
            if (typeof part.value === 'string') return part.value;
            return h(part.value as any, props, slots);
        };

        const exists = computed<boolean>(() => part.value !== null);
        const loading = computed<boolean>(() => part.value === loadingSymbol);

        return { part, connected, exists, loading, component, subscription };
    }
}
